import { jsx, jsxs } from "react/jsx-runtime";
import { useState, useCallback, useMemo } from "react";
import ImageGallery from "react-image-gallery";
import classnames from "../../utils/classnames.js";
import carFallback from "../../assets/images/carFallback.svg.js";
import Icon from "../Icon/Icon.js";
import './Gallery.css';/* empty css             */
const Gallery = ({
  videos = [],
  images,
  className,
  showNav = false,
  showFullscreenButton = false,
  showPlayButton = false,
  autoPlay = false
}) => {
  const [videoActiveIndex, setVideoActiveIndex] = useState(null);
  const [slideIndex, setSlideIndex] = useState(0);
  const renderVideos = useCallback(
    ({ original, title, url, index }) => {
      return /* @__PURE__ */ jsx("div", { children: videoActiveIndex === index ? /* @__PURE__ */ jsx("div", { className: classnames("perxis-auto-gallery-video-wrapper"), children: /* @__PURE__ */ jsx("iframe", { allow: "autoplay", title, src: url, allowFullScreen: true }) }) : /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: () => setVideoActiveIndex(index),
            className: classnames("perxis-auto-gallery-video__play"),
            children: /* @__PURE__ */ jsx(Icon, { name: "play", size: 24, sticker: "gallery-play-icon" })
          }
        ),
        /* @__PURE__ */ jsx("img", { className: "image-gallery-image", alt: title, src: original })
      ] }) });
    },
    [videoActiveIndex]
  );
  const renderThumb = useCallback(({ thumbnail, title }) => {
    return /* @__PURE__ */ jsxs("div", { className: "image-gallery-thumbnail-inner", children: [
      /* @__PURE__ */ jsx("div", { tabIndex: 0, role: "button", className: classnames("perxis-auto-gallery-thumbnail-video__play"), children: /* @__PURE__ */ jsx(Icon, { name: "play", size: 16, sticker: "gallery-play-icon" }) }),
      /* @__PURE__ */ jsx(
        "img",
        {
          className: classnames(["perxis-auto-gallery-thumbnail-video", "image-gallery-thumbnail-image"]),
          alt: title,
          src: thumbnail
        }
      )
    ] });
  }, []);
  const slides = useMemo(() => {
    const items = [];
    for (let i = 0; i < videos.length; i++) {
      const video = videos[i];
      items.push({
        original: video.preview.original || carFallback,
        thumbnail: video.preview.thumbnail || video.preview.original,
        srcSet: video.preview.srcSet,
        sizes: video.preview.sizes,
        originalAlt: video.preview.title,
        thumbnailAlt: video.preview.title,
        renderItem: ({ index }) => {
          return renderVideos({
            original: video.preview.original,
            title: video.preview.title,
            url: video.url,
            index
          });
        },
        renderThumbInner: () => renderThumb({
          thumbnail: video.preview.thumbnail,
          title: video.preview.title
        })
      });
    }
    for (let i = 0; i < images.length; i++) {
      const image = images[i];
      const item = {
        original: image.original || carFallback,
        thumbnail: image.thumbnail || image.original || carFallback,
        sizes: image.sizes,
        srcSet: image.srcSet,
        originalAlt: image.title,
        thumbnailAlt: image.title
      };
      if (i > 0) {
        items.push(item);
      } else {
        items.unshift(item);
      }
    }
    const itemsWithIndex = items.map((item, index) => ({
      ...item,
      index
    }));
    return itemsWithIndex;
  }, [images, videos, renderVideos, renderThumb]);
  if (slides && slides.length) {
    const showThumbnails = slides.length > 1;
    return /* @__PURE__ */ jsx("div", { className: classnames("perxis-auto-gallery", className), children: /* @__PURE__ */ jsx(
      ImageGallery,
      {
        onImageError: (e) => {
          e.currentTarget.src = carFallback;
          e.currentTarget.srcset = "";
          e.currentTarget.onerror = null;
        },
        onThumbnailError: (e) => {
          e.currentTarget.src = carFallback;
          e.currentTarget.srcset = "";
          e.currentTarget.onerror = null;
        },
        onSlide: (index) => {
          setSlideIndex(index);
          setVideoActiveIndex(null);
        },
        items: slides,
        startIndex: slideIndex,
        autoPlay,
        showNav,
        showPlayButton,
        renderLeftNav: (onClick, disabled) => {
          return /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              onClick,
              disabled,
              className: classnames("perxis-auto-gallery__nav-button perxis-auto-gallery__nav-button-left", className),
              children: /* @__PURE__ */ jsx(Icon, { name: "left", size: 36, sticker: "gallery-left-arrow-icon" })
            }
          );
        },
        renderRightNav: (onClick, disabled) => {
          return /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              onClick,
              disabled,
              className: classnames("perxis-auto-gallery__nav-button perxis-auto-gallery__nav-button-right", className),
              children: /* @__PURE__ */ jsx(Icon, { name: "right", size: 36, sticker: "gallery-right-arrow-icon" })
            }
          );
        },
        renderPlayPauseButton: (onClick, isPlaying) => {
          const iconName = isPlaying ? "pause" : "play";
          return /* @__PURE__ */ jsx("button", { type: "button", onClick, className: classnames("perxis-auto-gallery__play", className), children: /* @__PURE__ */ jsx(Icon, { name: iconName, size: 36, sticker: `gallery-${iconName}-icon` }) });
        },
        renderFullscreenButton: (onClick, isFullscreen) => {
          const iconName = isFullscreen ? "minimize" : "expand";
          return /* @__PURE__ */ jsx("button", { type: "button", onClick, className: classnames("perxis-auto-gallery__fullscreen", className), children: /* @__PURE__ */ jsx(Icon, { name: iconName, size: 36, sticker: `gallery-${iconName}-icon` }) });
        },
        showFullscreenButton,
        showThumbnails,
        onErrorImageURL: carFallback
      }
    ) });
  }
  return null;
};
export {
  Gallery as default
};
//# sourceMappingURL=Gallery.js.map
